import { cn } from "@/lib/utils";

export const NA = "Not Available";
export const isVal = (v) => v && v !== NA && v !== "";

export function BandBadge({ band }) {
  const map = {
    "High Priority": "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30",
    "Medium Priority": "bg-amber-500/15 text-amber-700 ring-amber-500/30",
    "Low Priority": "bg-slate-400/15 text-slate-600 ring-slate-400/30",
  };
  return (
    <span data-testid="band-badge" className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset", map[band] || map["Low Priority"])}>
      {band || "—"}
    </span>
  );
}

export function StatusBadge({ status }) {
  const map = {
    Discovered: "bg-sky-500/15 text-sky-700 ring-sky-500/30",
    Verified: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30",
    Contacted: "bg-violet-500/15 text-violet-700 ring-violet-500/30",
    Onboarded: "bg-teal-500/15 text-teal-700 ring-teal-500/30",
    Rejected: "bg-rose-500/15 text-rose-700 ring-rose-500/30",
  };
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset", map[status] || map.Discovered)}>
      {status || "Discovered"}
    </span>
  );
}

export function ScoreRing({ value, size = 44 }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value || 0));
  const color = pct >= 80 ? "#059669" : pct >= 60 ? "#d97706" : "#94a3b8";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <span className="absolute text-[11px] font-bold text-slate-700">{pct}</span>
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, accent = "teal" }) {
  const accents = {
    teal: "bg-teal-500/10 text-teal-600",
    emerald: "bg-emerald-500/10 text-emerald-600",
    amber: "bg-amber-500/10 text-amber-600",
    sky: "bg-sky-500/10 text-sky-600",
    violet: "bg-violet-500/10 text-violet-600",
    rose: "bg-rose-500/10 text-rose-600",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mt-2 text-3xl font-extrabold tabular-nums text-slate-900">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
        </div>
        {Icon && (
          <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl", accents[accent])}>
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </div>
        )}
      </div>
    </div>
  );
}

export function SectionHead({ title, desc, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900">{title}</h1>
        {desc && <p className="mt-1 text-sm text-slate-500">{desc}</p>}
      </div>
      {actions}
    </div>
  );
}

export function Val({ v, href }) {
  if (!isVal(v)) return <span className="text-slate-300">—</span>;
  if (href) return <a href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline break-all">{v}</a>;
  return <span>{v}</span>;
}
